import React, { useState, useEffect } from 'react';
import { X, MessageSquare, AlertCircle, CheckCircle } from 'lucide-react';
import { vendorInquiryApi } from '../services/api';
import type { FairInquiryCreate, InquiryType, InquiryPriority, Vendor } from '../types';

interface FairInquiryModalProps {
  isOpen: boolean;
  onClose: () => void;
  fairId: number;
  fairName: string;
  vendors: Vendor[];  // User's vendor profiles
  reservationId?: number;  // Optional linked reservation
}

const FairInquiryModal: React.FC<FairInquiryModalProps> = ({
  isOpen,
  onClose,
  fairId,
  fairName,
  vendors,
  reservationId,
}) => {
  const [vendorId, setVendorId] = useState<number>(0);
  const [inquiryType, setInquiryType] = useState<InquiryType>('general');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [priority, setPriority] = useState<InquiryPriority>('normal');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      // Pre-select first vendor if available
      if (vendors.length > 0 && vendorId === 0) {
        setVendorId(vendors[0].id);
      }
      setSubject('');
      setMessage('');
      setInquiryType('general');
      setPriority('normal');
      setError(null);
      setSuccess(false);
    }
  }, [isOpen, vendors, vendorId]);

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!vendorId) {
      setError('Please select a vendor profile');
      return;
    }
    if (!subject.trim()) {
      setError('Please enter a subject');
      return;
    }
    if (!message.trim()) {
      setError('Please enter a message');
      return;
    }

    setIsLoading(true);

    try {
      const data: FairInquiryCreate = {
        vendor_id: vendorId,
        fair_id: fairId,
        inquiry_type: inquiryType,
        subject: subject.trim(),
        message: message.trim(),
        priority,
        reservation_id: reservationId,
      };

      await vendorInquiryApi.createInquiry(data);
      setSuccess(true);

      // Close modal after short delay
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err: unknown) {
      console.error('Failed to create inquiry:', err);
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { detail?: string } } };
        setError(axiosError.response?.data?.detail || 'Failed to create inquiry. Please try again.');
      } else {
        setError('Failed to create inquiry. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center space-x-2">
            <MessageSquare className="h-6 w-6 text-secondary" />
            <h2 className="text-xl font-semibold text-gray-900">Contact Fair Manager</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={isLoading}
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Success Message */}
        {success && (
          <div className="p-4 m-6 bg-green-50 border border-green-200 rounded-lg flex items-start space-x-3">
            <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
            <div>
              <h3 className="font-medium text-green-900">Inquiry Submitted!</h3>
              <p className="text-sm text-green-700 mt-1">
                The fair manager has been notified and will respond shortly.
              </p>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="p-4 m-6 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-3">
            <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
            <div>
              <h3 className="font-medium text-red-900">Error</h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
        )}

        {/* Form */}
        {!success && (
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Fair Name (Read-only) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fair
              </label>
              <input
                type="text"
                value={fairName}
                disabled
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600"
              />
            </div>

            {/* Vendor Selection */}
            <div>
              <label htmlFor="vendor" className="block text-sm font-medium text-gray-700 mb-1">
                Your Vendor Profile <span className="text-red-500">*</span>
              </label>
              <select
                id="vendor"
                value={vendorId}
                onChange={(e) => setVendorId(parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                required
                disabled={isLoading}
              >
                <option value={0}>Select a vendor profile...</option>
                {vendors.map((vendor) => (
                  <option key={vendor.id} value={vendor.id}>
                    {vendor.business_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Inquiry Type */}
            <div>
              <label htmlFor="inquiryType" className="block text-sm font-medium text-gray-700 mb-1">
                Inquiry Type <span className="text-red-500">*</span>
              </label>
              <select
                id="inquiryType"
                value={inquiryType}
                onChange={(e) => setInquiryType(e.target.value as InquiryType)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                required
                disabled={isLoading}
              >
                <option value="general">General Question</option>
                <option value="reservation">Reservation Issue</option>
                <option value="payment">Payment Question</option>
                <option value="technical">Technical Problem</option>
                <option value="complaint">Complaint</option>
                <option value="other">Other</option>
              </select>
            </div>

            {/* Priority */}
            <div>
              <label htmlFor="priority" className="block text-sm font-medium text-gray-700 mb-1">
                Priority
              </label>
              <select
                id="priority"
                value={priority}
                onChange={(e) => setPriority(e.target.value as InquiryPriority)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                disabled={isLoading}
              >
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
              <p className="mt-1 text-sm text-gray-500">
                Set to High or Urgent only for time-sensitive issues
              </p>
            </div>

            {/* Subject */}
            <div>
              <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
                Subject <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Brief summary of your question or issue"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                required
                maxLength={200}
                disabled={isLoading}
              />
              <p className="mt-1 text-sm text-gray-500">
                {subject.length}/200 characters
              </p>
            </div>

            {/* Message */}
            <div>
              <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                Message <span className="text-red-500">*</span>
              </label>
              <textarea
                id="message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Provide details about your question or issue"
                rows={6}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary resize-none"
                required
                maxLength={2000}
                disabled={isLoading}
              />
              <p className="mt-1 text-sm text-gray-500">
                {message.length}/2000 characters
              </p>
            </div>

            {/* Linked Reservation (if provided) */}
            {reservationId && (
              <div className="p-4 bg-moonbone border border-teal-200 rounded-lg">
                <p className="text-sm text-ink">
                  <strong>Linked to Reservation:</strong> #{reservationId}
                </p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    <MessageSquare className="h-4 w-4" />
                    <span>Send Inquiry</span>
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default FairInquiryModal;
